/**
 * Blog index: category filter (native redirect) + load more on scroll.
 * Shows skeleton placeholders with GSAP pulse while loading, then animates in new cards.
 */
import gsap from "gsap";

const SKELETON_COUNT = 12;
const CARD_ANIM_DURATION = 0.4;
const CARD_STAGGER = 0.04;
const CARD_ANIM_DELAY = 0.06;
/** Minimum time skeletons stay visible before cards animate in (ms) */
const MIN_SKELETON_MS = 1200;

function getSkeletonHtml() {
	const parts = [];
	for (let i = 0; i < SKELETON_COUNT; i++) {
		parts.push(
			`<div class="blog-card-skeleton" aria-hidden="true">
				<span class="blog-card-skeleton__image"></span>
				<span class="blog-card-skeleton__body">
					<span class="blog-card-skeleton__line blog-card-skeleton__line--short"></span>
					<span class="blog-card-skeleton__line"></span>
					<span class="blog-card-skeleton__line"></span>
				</span>
			</div>`
		);
	}
	return parts.join("");
}

export function initBlogLoadMore() {
	const grid = document.querySelector(".js-blog-grid");
	const categorySelect = document.getElementById("blog-filter-category");
	if (!grid) return;

	const blogUrl = grid.dataset.blogUrl || "";
	const baseUrl = blogUrl.replace(/\?.*$/, "");

	if (categorySelect) {
		categorySelect.addEventListener("change", () => {
			const slug = categorySelect.value;
			window.location.href = slug ? `${baseUrl}?category_name=${encodeURIComponent(slug)}` : baseUrl;
		});
	}

	const ajaxUrl = typeof window.glaceV2 !== "undefined" && window.glaceV2.ajaxUrl ? window.glaceV2.ajaxUrl : null;
	if (!ajaxUrl) return;

	let loading = false;
	let currentPage = 1;
	let maxPages = parseInt(grid.dataset.maxPages, 10) || 1;
	let category = grid.dataset.category || "";
	let skeletonTween = null;

	function addSkeletons() {
		const wrap = document.createElement("div");
		wrap.className = "blog-load-more-skeletons";
		wrap.setAttribute("aria-live", "polite");
		wrap.innerHTML = getSkeletonHtml();
		grid.appendChild(wrap);

		const skeletons = wrap.querySelectorAll(".blog-card-skeleton");
		gsap.set(skeletons, { opacity: 0.4 });
		skeletonTween = gsap.to(skeletons, {
			opacity: 0.75,
			duration: 0.8,
			stagger: 0.04,
			repeat: -1,
			yoyo: true,
			ease: "sine.inOut",
		});
		return wrap;
	}

	function removeSkeletons(wrap) {
		if (skeletonTween) {
			skeletonTween.kill();
			skeletonTween = null;
		}
		if (wrap && wrap.parentNode) {
			gsap.to(wrap, { opacity: 0, duration: 0.2, onComplete: () => wrap.remove() });
		}
	}

	function loadMore() {
		if (loading || currentPage >= maxPages) return;
		loading = true;

		const skeletonWrap = addSkeletons();

		const nextPage = currentPage + 1;
		const url = `${ajaxUrl}?action=blog_load_more&paged=${nextPage}${category ? `&category_name=${encodeURIComponent(category)}` : ""}`;

		const fetchPromise = fetch(url, { method: "GET" }).then((res) => res.json());
		const delayPromise = new Promise((resolve) => setTimeout(resolve, MIN_SKELETON_MS));

		Promise.all([fetchPromise, delayPromise])
			.then(([data]) => {
				removeSkeletons(skeletonWrap);

				if (data.success && data.data && data.data.html) {
					const temp = document.createElement("div");
					temp.innerHTML = data.data.html;
					const cards = temp.querySelectorAll(".blog-card");
					if (cards.length > 0) {
						gsap.set(cards, { opacity: 0, y: 24 });
						cards.forEach((el) => grid.appendChild(el));
						gsap.to(cards, {
							opacity: 1,
							y: 0,
							duration: CARD_ANIM_DURATION,
							stagger: CARD_STAGGER,
							delay: CARD_ANIM_DELAY,
							ease: "power2.out",
						});
					}
					currentPage = nextPage;
					if (data.data.has_more === false) maxPages = currentPage;
				}
			})
			.catch(() => removeSkeletons(skeletonWrap))
			.finally(() => {
				loading = false;
			});
	}

	let scrollTicking = false;
	function onScroll() {
		if (scrollTicking || !grid.isConnected) return;
		scrollTicking = true;
		requestAnimationFrame(() => {
			scrollTicking = false;
			const gridBottom = grid.offsetTop + grid.offsetHeight;
			const viewBottom = window.scrollY + window.innerHeight + 400;
			if (viewBottom >= gridBottom) loadMore();
		});
	}

	window.addEventListener("scroll", onScroll, { passive: true });
}
